import axios from 'axios'
import { AUTH_CONFIG, STORAGE_KEYS } from '../constants/app.constants'
import { decodeJWT } from '../utils/jwt.utils'

/**
 * Access-token refresh for the backoffice session.
 *
 * Ported from Backoffice-Frontend (`src/api/users/refresh-token.ts`), adapted to this
 * repo's storage model: Backoffice-Frontend keeps one `adminuser` JSON blob and
 * read-modify-writes it, whereas QLM uses separate flat localStorage keys.
 *
 * Two guards bound how often this reaches the network, and both matter:
 *
 *   - single-flight, for callers that OVERLAP in time (the proactive timer firing while
 *     a 401 retry is already in flight). Without it, two refreshes race and can rotate
 *     the refresh token out from under each other.
 *   - a cooldown, for callers that arrive BACK-TO-BACK. Single-flight does nothing for
 *     these, because the first request has already settled. This is what stops several
 *     sequential 401s from becoming several refreshes.
 */

// A refresh that succeeded this recently counts as "just done": a caller asking again
// inside this window gets the stored token instead of a new request.
const REFRESH_COOLDOWN_MS = 30_000

// Dedicated bare axios instance. Deliberately NOT `apiClient` or a BaseService client:
// those carry the 401 response interceptor that calls this function, so reusing one
// would let a failing refresh recurse into itself.
const refreshHttp = axios.create()

export const getStoredToken = (): string | null =>
  localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)

export const getStoredRefreshToken = (): string | null =>
  localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

/**
 * Milliseconds until `token` expires, or null when it carries no usable `exp` (missing
 * claim, or not a decodable JWT). Negative once it has expired.
 */
export const msUntilExpiry = (token: string): number | null => {
  const decoded = decodeJWT(token)
  const exp = decoded?.exp
  if (!exp) return null
  return exp * 1000 - Date.now()
}

/**
 * Whether `token` is expired, or expires within `skewSeconds`.
 *
 * A token with no readable `exp` counts as expired: we cannot prove it is live, and the
 * caller (a 401 handler) is already holding evidence that it is not.
 */
export const isTokenExpired = (token: string, skewSeconds = 0): boolean => {
  const ms = msUntilExpiry(token)
  if (ms === null) return true
  return ms <= skewSeconds * 1000
}

/** Remove every trace of the session. Used when refresh proves the session is dead. */
export const clearStoredSession = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.USER_DATA)
}

let refreshInFlight: Promise<RefreshOutcome> | null = null
// When the last refresh request came back with a token. Drives the cooldown.
let lastRefreshAt = 0

/** Why a refresh was asked for — surfaced in the log line below. */
export type RefreshReason = 'proactive' | '401-retry'

/**
 * The result of a refresh attempt.
 *
 * Distinguishing `revoked` from `unavailable` is essential, because the proactive caller
 * runs every 60 seconds as a revocation poll. Collapsing both into "no token" — as a
 * simple `string | null` return does — means a single 5xx or a dropped connection logs
 * the admin out mid-edit. Only a definitive 401/403 proves the session is actually dead.
 */
export type RefreshOutcome =
  | { status: 'refreshed'; token: string }
  /** 401/403 — account blocked, or the refresh token is expired/revoked. Log out. */
  | { status: 'revoked' }
  /** 5xx, timeout, offline, or a malformed body. Keep the session and try again. */
  | { status: 'unavailable' }

const doRefresh = async (reason: RefreshReason): Promise<RefreshOutcome> => {
  const refreshToken = getStoredRefreshToken()
  // No refresh token at all — nothing to exchange. Treated as revoked rather than
  // unavailable: retrying cannot help, because there is nothing to retry with.
  if (!refreshToken) return { status: 'revoked' }

  try {
    // One line per request that ACTUALLY goes out — deduped and cooled-down callers are
    // silent. This is what makes a refresh storm legible in production without a
    // rebuild, which is how the same bug was diagnosed in the other frontends.
    console.warn('[auth] refreshing access token', { reason })

    const { data } = await refreshHttp.post(
      `${AUTH_CONFIG.BASE_URL}${AUTH_CONFIG.REFRESH_ENDPOINT}`,
      { refresh_token: refreshToken },
      { timeout: AUTH_CONFIG.TIMEOUT }
    )

    // The endpoint returns `access_token`; accept `token` too, matching the tolerance
    // AuthService.login already applies to the login response.
    const newToken: string | undefined = data?.access_token || data?.token
    // 2xx with no token is a server contract problem, not proof the session is dead.
    if (!newToken) return { status: 'unavailable' }

    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken)
    // Store the rotated refresh token when one comes back. If it is ignored and the
    // backend rotates, the old one becomes invalid and the next refresh 401s.
    if (data?.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
    }

    lastRefreshAt = Date.now()
    return { status: 'refreshed', token: newToken }
  } catch (error) {
    // Classify, because the caller's response differs completely:
    //
    //   401/403 → the account was blocked, or the refresh token is expired/revoked.
    //             QL-User returns 401 from the `user.status !== ACTIVE` check, which is
    //             exactly the signal the revocation poll exists to catch. Log out.
    //
    //   anything else (5xx, timeout, DNS, offline — no `response` at all) → the server
    //             could not answer. It says NOTHING about the session, so keep it and
    //             try again on the next tick. Logging out here would mean a flaky
    //             network ejects an admin mid-article every minute.
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 401 || status === 403) {
      console.warn('[auth] refresh rejected — session revoked', { reason, status })
      return { status: 'revoked' }
    }
    console.warn('[auth] refresh unavailable — keeping session', { reason, status })
    return { status: 'unavailable' }
  }
}

interface RefreshOptions {
  /**
   * Bypass the cooldown and always reach the server.
   *
   * Used ONLY by the proactive poll. The cooldown returns the stored token without a
   * request, which is right for a keepalive but wrong for a revocation check — a
   * suppressed poll silently skips the very thing it exists to detect. Safe because the
   * poll fires at most once every 60 seconds, so this cannot produce a storm.
   *
   * Single-flight still applies: joining a request already in flight is a genuine server
   * round trip, so it counts as a real check.
   */
  force?: boolean
}

/**
 * Refresh the access token using the stored refresh token, updating localStorage on
 * success. See `RefreshOutcome` — callers must distinguish `revoked` from `unavailable`.
 */
export const refreshAccessToken = (
  reason: RefreshReason = '401-retry',
  { force = false }: RefreshOptions = {}
): Promise<RefreshOutcome> => {
  if (refreshInFlight) return refreshInFlight

  const stored = getStoredToken()
  if (
    !force &&
    stored &&
    Date.now() - lastRefreshAt < REFRESH_COOLDOWN_MS &&
    // Small skew only. A live token is enough to skip the request, but anything larger
    // would also suppress a near-expiry refresh.
    !isTokenExpired(stored, 5)
  ) {
    return Promise.resolve({ status: 'refreshed', token: stored })
  }

  const p = (async () => {
    try {
      return await doRefresh(reason)
    } finally {
      refreshInFlight = null
    }
  })()
  refreshInFlight = p
  return p
}
